/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { useState } from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import {
  AreaColorMap,
  AreaHotkeyMap,
  AreaMaterialsMap,
  KeyboardKey,
  UNMAPPED_AREA_COLOR,
} from "./scrapEyeLayoutCommon";
import { formatArea } from "components/identifyEventsTool/utils/formatArea";
import {
  GridCell as GridCellType,
  GridLayoutArea,
  GridLayoutData,
} from "src/store/api/generatedApi";

const CELL_SIZE = 40;
const INSET = 3;

type LabelBounds = Readonly<{
  minRow: number;
  maxRow: number;
  minCol: number;
  maxCol: number;
}>;

const getLabelBounds = (area: GridLayoutArea): LabelBounds => {
  const rows = area.cells.reduce((acc, cell) => {
    if (!acc.has(cell.row)) acc.set(cell.row, []);
    acc.get(cell.row)?.push(cell.col);
    return acc;
  }, new Map<number, number[]>());

  const rowSizeCounter = [...rows.values()]
    .map((cols) => cols.length)
    .reduce((acc, size) => {
      acc.set(size, (acc.get(size) ?? 0) + 1);
      return acc;
    }, new Map<number, number>());

  const mostFrequentRowSize = [...rowSizeCounter.entries()].reduce(
    (acc, [size, count]) => {
      if (count > acc[1]) return [size, count];
      return acc;
    },
    [0, 0]
  )[0];

  const bounds = [...rows.entries()].reduce(
    (acc, [row, cols]) => {
      if (cols.length === mostFrequentRowSize) {
        acc.minRow = Math.min(acc.minRow, row);
        acc.maxRow = Math.max(acc.maxRow, row);
        cols.forEach((col) => {
          acc.minCol = Math.min(acc.minCol, col);
          acc.maxCol = Math.max(acc.maxCol, col);
        });
      }
      return acc;
    },
    { minRow: Infinity, maxRow: -Infinity, minCol: Infinity, maxCol: -Infinity }
  );
  return bounds;
};

const getCellLocation = (cell: GridCellType): string =>
  `${cell.row}:${cell.col}`;

type Neighbours = Readonly<{
  top: boolean;
  bottom: boolean;
  left: boolean;
  right: boolean;
}>;

const getCellNeighbours = (
  cell: GridCellType,
  cellLocations: Set<string>
): Neighbours => ({
  top: cellLocations.has(`${cell.row - 1}:${cell.col}`),
  bottom: cellLocations.has(`${cell.row + 1}:${cell.col}`),
  left: cellLocations.has(`${cell.row}:${cell.col - 1}`),
  right: cellLocations.has(`${cell.row}:${cell.col + 1}`),
});

type InnerCorners = Readonly<{
  topLeft: boolean;
  topRight: boolean;
  bottomLeft: boolean;
  bottomRight: boolean;
}>;

const getInnerCorners = (
  cell: GridCellType,
  cellLocations: Set<string>,
  neighbours: Neighbours
): InnerCorners => ({
  topLeft:
    neighbours.top &&
    neighbours.left &&
    !cellLocations.has(`${cell.row - 1}:${cell.col - 1}`),
  topRight:
    neighbours.top &&
    neighbours.right &&
    !cellLocations.has(`${cell.row - 1}:${cell.col + 1}`),
  bottomLeft:
    neighbours.bottom &&
    neighbours.left &&
    !cellLocations.has(`${cell.row + 1}:${cell.col - 1}`),
  bottomRight:
    neighbours.bottom &&
    neighbours.right &&
    !cellLocations.has(`${cell.row + 1}:${cell.col + 1}`),
});

const CORNER_POSITIONS = [
  {
    key: "topLeft",
    sx: (borderWidth: number, borderColor: string) => ({
      top: 0,
      left: 0,
      borderBottom: borderWidth,
      borderRight: borderWidth,
      borderColor: borderColor,
    }),
  },
  {
    key: "topRight",
    sx: (borderWidth: number, borderColor: string) => ({
      top: 0,
      right: 0,
      borderBottom: borderWidth,
      borderLeft: borderWidth,
      borderColor: borderColor,
    }),
  },
  {
    key: "bottomLeft",
    sx: (borderWidth: number, borderColor: string) => ({
      bottom: 0,
      left: 0,
      borderTop: borderWidth,
      borderRight: borderWidth,
      borderColor: borderColor,
    }),
  },
  {
    key: "bottomRight",
    sx: (borderWidth: number, borderColor: string) => ({
      bottom: 0,
      right: 0,
      borderTop: borderWidth,
      borderLeft: borderWidth,
      borderColor: borderColor,
    }),
  },
] as const;

type GridCellItemProps = Readonly<{
  cell: GridCellType;
  area: GridLayoutArea;
  neighbours: Neighbours;
  innerCorners: InnerCorners;
  onSelectArea?: (area: string) => void;
  onHoverArea: (area: string | null) => void;
  isSelectable: boolean;
  areaColors?: AreaColorMap;
  selectedArea?: string | null;
  isHovered: boolean;
}>;

const GridCellItem = ({
  cell,
  area,
  neighbours,
  innerCorners,
  onSelectArea,
  onHoverArea,
  isSelectable,
  areaColors,
  selectedArea,
  isHovered,
}: GridCellItemProps) => {
  const isSelected = selectedArea === area.area_id;
  const isDimmed = selectedArea != null && !isSelected;
  const color = areaColors?.[area.area_id] ?? UNMAPPED_AREA_COLOR;
  const borderWidth = isSelected || isHovered ? 2 : 1;
  const borderColor = isSelected || isHovered ? "primary.main" : "divider";

  return (
    <Box
      onClick={() => isSelectable && !isDimmed && onSelectArea?.(area.area_id)}
      onMouseEnter={() => {
        onHoverArea(area.area_id);
      }}
      onMouseLeave={() => {
        onHoverArea(null);
      }}
      sx={{
        gridRow: cell.row,
        gridColumn: cell.col,
        position: "relative",
        cursor:
          isSelectable && onSelectArea && !isDimmed ? "pointer" : "default",
        opacity: isDimmed ? 0.35 : 1,
        filter: isDimmed ? "grayscale(0.7)" : undefined,
        transition: "opacity 0.15s ease-in-out, filter 0.15s ease-in-out",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: neighbours.top ? 0 : INSET,
          bottom: neighbours.bottom ? 0 : INSET,
          left: neighbours.left ? 0 : INSET,
          right: neighbours.right ? 0 : INSET,
          bgcolor: color,
          borderTop: !neighbours.top ? `${borderWidth}px solid` : "none",
          borderBottom: !neighbours.bottom ? `${borderWidth}px solid` : "none",
          borderLeft: !neighbours.left ? `${borderWidth}px solid` : "none",
          borderRight: !neighbours.right ? `${borderWidth}px solid` : "none",
          borderColor,
          transition: "border-color 0.15s ease-in-out",
        }}
      />
      {CORNER_POSITIONS.filter(({ key }) => innerCorners[key]).map(
        ({ key, sx }) => (
          <Box
            key={key}
            sx={{
              position: "absolute",
              ...sx(borderWidth, borderColor),
              width: INSET,
              height: INSET,
              bgcolor: "background.default",
              zIndex: 1,
            }}
          />
        )
      )}
    </Box>
  );
};

type AreaLabelProps = Readonly<{
  area: GridLayoutArea;
  areaHotkeys?: AreaHotkeyMap;
  areaMaterials?: AreaMaterialsMap;
}>;

const AreaLabel = ({ area, areaHotkeys, areaMaterials }: AreaLabelProps) => {
  const { minRow, maxRow, minCol, maxCol } = getLabelBounds(area);
  const hotkey = areaHotkeys?.[area.area_id];
  const material = areaMaterials?.[area.area_id];
  const areaRows = maxRow - minRow + 1;
  const showInlineMaterial = areaRows >= 2;
  const showTooltip = !!material && !showInlineMaterial;

  const label = (
    <Box
      sx={{
        gridRow: `${minRow} / ${maxRow + 1}`,
        gridColumn: `${minCol} / ${maxCol + 1}`,
        position: "relative",
        pointerEvents: showTooltip ? "auto" : "none",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 0.25,
        overflow: "hidden",
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: "white",
          fontWeight: "bold",
          textShadow: "1px 1px 2px rgba(0,0,0,0.5)",
          fontSize: "0.65rem",
          textAlign: "center",
          lineHeight: 1.1,
          px: 0.5,
        }}
      >
        {area.display_name ?? formatArea(area.area_id)}
      </Typography>
      {hotkey ? <KeyboardKey hotkey={hotkey} size={16} /> : null}
      {showInlineMaterial && material ? (
        <Typography
          variant="caption"
          sx={{
            color: "white",
            textShadow: "1px 1px 2px rgba(0,0,0,0.5)",
            fontSize: "0.5rem",
            textAlign: "center",
            lineHeight: 1,
            px: 0.25,
          }}
        >
          {material}
        </Typography>
      ) : null}
    </Box>
  );

  if (showTooltip) {
    return (
      <Tooltip title={material} arrow placement="top">
        {label}
      </Tooltip>
    );
  }

  return label;
};

type GridAreaProps = Readonly<{
  area: GridLayoutArea;
  onSelectArea?: (area: string) => void;
  onHoverArea: (area: string | null) => void;
  nonSelectableAreas: readonly string[];
  areaHotkeys?: AreaHotkeyMap;
  areaColors?: AreaColorMap;
  areaMaterials?: AreaMaterialsMap;
  selectedArea?: string | null;
  isHovered: boolean;
}>;

const GridArea = ({
  area,
  onSelectArea,
  onHoverArea,
  nonSelectableAreas,
  areaHotkeys,
  areaColors,
  areaMaterials,
  selectedArea,
  isHovered,
}: GridAreaProps) => {
  const isSelectable = !nonSelectableAreas.includes(area.area_id);
  const cellLocations = new Set(area.cells.map(getCellLocation));
  return (
    <>
      {area.cells.map((cell) => {
        const neighbours = getCellNeighbours(cell, cellLocations);
        const innerCorners = getInnerCorners(cell, cellLocations, neighbours);
        return (
          <GridCellItem
            key={`${area.area_id}:${cell.row}:${cell.col}`}
            cell={cell}
            area={area}
            neighbours={neighbours}
            innerCorners={innerCorners}
            onSelectArea={onSelectArea}
            onHoverArea={onHoverArea}
            isSelectable={isSelectable}
            areaColors={areaColors}
            selectedArea={selectedArea}
            isHovered={isHovered}
          />
        );
      })}
      <AreaLabel
        area={area}
        areaHotkeys={areaHotkeys}
        areaMaterials={areaMaterials}
      />
    </>
  );
};

export const GridStyleLayout = ({
  layout,
  onSelectArea,
  nonSelectableAreas,
  areaHotkeys,
  areaColors,
  areaMaterials,
  selectedArea,
}: {
  layout: GridLayoutData;
  onSelectArea?: (area: string) => void;
  nonSelectableAreas: readonly string[];
  areaHotkeys?: AreaHotkeyMap;
  areaColors?: AreaColorMap;
  areaMaterials?: AreaMaterialsMap;
  selectedArea?: string | null;
}) => {
  const [hoveredArea, setHoveredArea] = useState<string | null>(null);

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateRows: `repeat(${layout.rows}, ${CELL_SIZE}px)`,
        gridTemplateColumns: `repeat(${layout.cols}, ${CELL_SIZE}px)`,
        bgcolor: "background.default",
        borderRadius: 1,
        p: 2,
      }}
    >
      {layout.areas.map((area) => (
        <GridArea
          key={area.area_id}
          area={area}
          onSelectArea={onSelectArea}
          onHoverArea={(hovered) => {
            setHoveredArea(hovered === null ? null : area.area_id);
          }}
          isHovered={hoveredArea === area.area_id}
          nonSelectableAreas={nonSelectableAreas}
          areaHotkeys={areaHotkeys}
          areaColors={areaColors}
          areaMaterials={areaMaterials}
          selectedArea={selectedArea}
        />
      ))}
    </Box>
  );
};
